import BillDateWithTimezone from '@/common/components/BillDateWithTimezone';
import Button from '@/common/components/Button';
import { billingBillStatus } from '@/common/constants/statusBills.constants';
import { formatDatePeriodFriendlyName } from '@/common/utils/dateTransformer';
import { formatCurrency } from '@/common/utils/formatters';
import { transformToNumber } from '@/common/utils/transformers';
import { ClientDetails } from '@/features/clientBills/components/ClientDetails';
import { strategy as billKindStrategy } from '@/features/clientBills/constants/billKind.constant';
import { strategy as clientKindStrategy } from '@/features/clientBills/constants/clientKind.constant';
import { ClientBillData } from '@/features/clientBills/types/clientBills.type';
import { AlertCircleIcon, ArrowBigRightIcon } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

export class ClientBillsTransformer {
  static tableTransformer() {
    return (companyBills: ClientBillData[]) => {
      return companyBills.map((bill) => ({
        _className: bill.status === 'rejected_by_company' && 'bg-red-op-2',
        status: (
          <div className="flex items-center gap-2">
            <p>
              {bill.status ? billingBillStatus.find(({ value }) => value === bill?.status)?.label : ''}
            </p>

            {bill.rejected_by_company_at && (
              <>
                <AlertCircleIcon
                  strokeWidth={1.5}
                  className="text-red-500 size-4"
                  data-tooltip-id="bill-rejected-tooltip"
                />

                <Tooltip id="bill-rejected-tooltip" className="text-sm z-50">
                  Fatura rejeitada
                </Tooltip>
              </>
            )}
          </div>
        ),
        number: bill.number,
        client_kind: bill.client_company?.kind
          ? clientKindStrategy[bill.client_company.kind as keyof typeof clientKindStrategy]
          : '',
        client_info: React.createElement(ClientDetails, { client: bill.client_company }),
        client_branch_display_name: bill.client_branch.display_name || '',
        credit_info: bill?.granted_credit ? formatCurrency(parseFloat(`${bill?.granted_credit}`)) : '',
        bill_kind: bill.kind ? billKindStrategy[bill.kind as keyof typeof billKindStrategy] : '',
        total_amount: bill?.total_amount ? formatCurrency(transformToNumber({ value: bill?.total_amount })) : '',
        opened_at: bill?.opened_at && (
          <BillDateWithTimezone
            date={bill.opened_at}
            timezone={bill.time_zone}
          />
        ),
        closed_at: bill?.closed_at && (
          <BillDateWithTimezone
            date={bill.closed_at}
            timezone={bill.time_zone}
          />
        ),
        period: formatDatePeriodFriendlyName({
          closedAt: bill?.closed_at,
          openedAt: bill?.opened_at,
          contractualCycle: bill?.contractual_cycle,
        }),
        due_at: bill?.due_at && (
          <BillDateWithTimezone
            date={bill.due_at}
            timezone={{
              id: 'america_sao_paulo',
              acronym: 'BRT',
              display_name: 'América - São Paulo',
              identifier: 'America/Sao_Paulo',
              offset: '-03:00',
            }}
            hideTimezone
          />
        ),
        actions: (
          <div className="flex justify-center items-center">
            <Link to={`/client-bills/${bill.id}`}>
              <Button name="access_client_bill_show" variant="link-white" className="w-full" operationKlassToCheckPermission='Admin::Billing::Bills::Show'>
                <ArrowBigRightIcon strokeWidth={1} />
              </Button>
            </Link>
          </div>
        ),
      }));
    };
  }

  static quickSearchModelTransformer() {
    return (bills: ClientBillData[]) => {
      return bills.map((bill) => ({
        route: `client-bills/${bill.id}`,
        items: [
          ['Status', billingBillStatus.find((option) => option.value === bill.status)?.label || ''],
          ['Código', bill.number],
          ['Tipo', bill.kind ? billKindStrategy[bill.kind as keyof typeof billKindStrategy] : ''],
          ['Tipo de empresa', bill.client_company?.kind ? clientKindStrategy[bill.client_company.kind as keyof typeof clientKindStrategy] : ''],
          ['Empresa', bill.client_company.general_information.fantasy_name],
        ],
      }));
    };
  }
}
